import { DndContext, useDraggable, useSensor, useSensors } from "@dnd-kit/core";
import { Coordinates, DragEndEvent } from "@dnd-kit/core/dist/types";
import { useState } from "react";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { Modal } from "antd";
import { ModalItemProps } from "./ModalContext";
import classnames from "classnames";
import { SmartPointerSensor } from "utils/libHelpers/SmartPointerSensor";

export const MODAL_DEFAULT_Z_INDEX = 1011;

interface DnDModalInContextProps {
  modal: ModalItemProps;
  index: number;
  hideModal: () => void;
  setConfirmOnCancel: (b: boolean) => void;
  setOnSave: (fn: () => void) => void;
  updateModalPosition: (position: ModalItemProps["relativePosition"]) => void;
}

export const DraggableModal = (
  props: Omit<DnDModalInContextProps, "updateModalPosition"> & { top: number; left: number }
) => {
  const { attributes, listeners, setNodeRef, transform /*setActivatorNodeRef*/, isDragging } = useDraggable({
    id: props.modal.uid,
    disabled: !props.modal.enableDnD,
  });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;
  return (
    <Modal
      key={props.modal.uid}
      open={props.modal.visible}
      {...props.modal.modalProps}
      onCancel={props.hideModal}
      destroyOnHidden={true}
      zIndex={props.modal.modalProps?.zIndex || MODAL_DEFAULT_Z_INDEX + props.index}
      modalRender={(modalComponent) => (
        <div
          ref={setNodeRef}
          style={{ ...style, position: "relative", top: props.top, left: props.left }}
          {...attributes}
          {...listeners}
          className={classnames("draggable-modal", { "is-dragging": isDragging })}
        >
          {modalComponent}
        </div>
      )}
    >
      {props.modal.component ? (
        <props.modal.component
          {...props.modal.componentProps}
          hideModal={props.hideModal}
          setConfirmOnCancel={props.setConfirmOnCancel}
          setOnSave={props.setOnSave}
        />
      ) : null}
    </Modal>
  );
};

export const DnDModalInContext = ({
  modal,
  index,
  hideModal,
  setConfirmOnCancel,
  setOnSave,
  updateModalPosition,
}: DnDModalInContextProps) => {
  // const activationConstraint = { distance: { x: 2, y: 2 } };
  const activationConstraint = { delay: 300, tolerance: 5 };

  const smartPointerSensor = useSensor(SmartPointerSensor, { activationConstraint });
  const sensors = useSensors(smartPointerSensor);
  const [{ x, y }, setCoordinates] = useState<Coordinates>({
    x: modal.relativePosition?.left || 0,
    y: modal.relativePosition?.top || 0,
  });

  const handleDragEnd = ({ delta, activatorEvent }: DragEndEvent) => {
    activatorEvent.preventDefault();
    const newTop = y + delta.y;
    const newLeft = x + delta.x;
    updateModalPosition({ top: newTop, left: newLeft });
    setCoordinates({
      x: newLeft,
      y: newTop,
    });
  };

  return (
    <DndContext sensors={sensors} modifiers={[restrictToWindowEdges]} id={modal.uid} onDragEnd={handleDragEnd}>
      <DraggableModal
        modal={modal}
        hideModal={hideModal}
        setConfirmOnCancel={setConfirmOnCancel}
        setOnSave={setOnSave}
        index={index}
        top={y}
        left={x}
      />
    </DndContext>
  );
};
